import * as mqtt from "precompiled-mqtt";
import { IConnectionSetting, IEventReportValues } from "./EventReportInterfaces";
import { MQTTEventsEnum } from "constants/constants";
import { uniqueClientId } from "mqtt/uniqueClientId";

const { connect } = mqtt;

const connectionsSettings = {
    protocol: "ws" as "ws",
    clientId: "osorno_poc_client_test",
    username: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
};

const defaultMQTTBrokerSettings: IConnectionSetting = {
    allow_anonymous: false,
    host: process.env.REACT_APP_MOSQUITTO_BROKER_URL || "mqtt://test.mosquitto.org:8081",
    user: process.env.REACT_APP_MOSQUITTO_BROKER_USER,
    password: process.env.REACT_APP_MOSQUITTO_BROKER_PASSWORD,
};

const eventReportTopic = process.env.REACT_APP_MQTT_EVENT_TOPIC || "osorno_event_reporting";

export const EventReportMQTTEntity = {
    publishEventReport: (
        values: IEventReportValues,
        options?: IConnectionSetting,
        clientId?: string,
        timeoutMs: number = 15000,
    ): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (!options) {
                options = defaultMQTTBrokerSettings;
            }
            const mosquittoUrl = options.host;
            const isWSS = mosquittoUrl.startsWith("wss");
            const client = connect(mosquittoUrl, {
                ...connectionsSettings,
                username: options.user,
                password: options.password,
                protocol: isWSS ? "wss" : "ws",
                clean: true,
                clientId: uniqueClientId(clientId || "event-report"),
                reconnectPeriod: 0,
            });

            const timeoutId = setTimeout(() => {
                client.end();
                reject(new Error("Operation timed out"));
            }, timeoutMs);

            const clearTimeoutAndResolve = () => {
                clearTimeout(timeoutId);
                client.end();
                resolve();
            };

            const clearTimeoutAndReject = (error: Error) => {
                clearTimeout(timeoutId);
                client.end();
                reject(error);
            };

            client.on(MQTTEventsEnum.ERROR, (err) => {
                clearTimeoutAndReject(new Error(`MQTT client error: ${err.message}`));
            });

            let handledConnect = false;
            client.on(MQTTEventsEnum.CONNECT, () => {
                if (handledConnect) {
                    return;
                }
                handledConnect = true;
                const eventReportValues = JSON.stringify(values);
                client.publish(eventReportTopic, eventReportValues, { qos: 1 }, (err, packet) => {
                    if (err) {
                        client.end();
                        clearTimeoutAndReject(new Error(`Failed to publish event: ${err.message}`));
                    } else if (packet) {
                        clearTimeoutAndResolve();
                    } else {
                        clearTimeoutAndReject(new Error("Message published, but no confirmation received"));
                    }
                });
            });

            client.on(MQTTEventsEnum.CLOSE, () => {
                clearTimeoutAndReject(
                    new Error(
                        handledConnect ? "Connection closed before publish was confirmed" : "MQTT not able to connect",
                    ),
                );
            });
        });
    },
    testMQTTBroker: (options: IConnectionSetting): Promise<void> => {
        return new Promise((res, rej) => {
            const mosquittoUrl = options.host;
            const isWSS = mosquittoUrl.startsWith("wss");
            const client = connect(mosquittoUrl, {
                ...connectionsSettings,
                username: options.user,
                password: options.password,
                reconnectPeriod: 0,
                connectTimeout: 10 * 1000,
                protocol: isWSS ? "wss" : "ws",
                clientId: uniqueClientId("broker-test"),
            });
            let isResolved = false;
            client.on(MQTTEventsEnum.ERROR, (err) => {
                // Force-close so a failed connect doesn't leave a dead socket.
                client.end(true);
                rej(err);
            });
            client.on(MQTTEventsEnum.CLOSE, () => {
                if (!isResolved) {
                    rej(new Error("Closed before connected"));
                }
            });
            client.on(MQTTEventsEnum.CONNECT, () => {
                isResolved = true;
                res();
                client.end();
            });
        });
    },
};
